import { ChevronDown, Clock, Loader2, Lock, Package, ShieldCheck, ShoppingCart, Sparkles, Unlock, Wallet } from 'lucide-react';
import { Fragment, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { buyPosition, buySubpack, fetchPublicLaunchStatus, fetchSubpackTiers, getPoolStatus } from '@/api/member';
import BannedBanner from '@/components/member/BannedBanner';
import CryptoDepositPanel from '@/components/member/widgets/CryptoDepositPanel';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { fmtUsd } from '@/lib/money';

// Day-0 / network-failure fallback ONLY. Live values come from
// ``fetchSubpackTiers()`` and override these on mount. Kept here so the
// dropdown never renders empty if the API call fails. Values mirror
// backend SUBPACK_TIERS in /app/backend/core/config.py.
const SUBPACK_TIERS_FALLBACK = [
  { tier_price_usd: 5, total_positions: 5, release_interval_hours: 32 },
  { tier_price_usd: 10, total_positions: 10, release_interval_hours: 16 },
  { tier_price_usd: 25, total_positions: 25, release_interval_hours: 8 },
  { tier_price_usd: 50, total_positions: 50, release_interval_hours: 4 },
  { tier_price_usd: 100, total_positions: 100, release_interval_hours: 2 },
  { tier_price_usd: 250, total_positions: 250, release_interval_hours: 1 },
  { tier_price_usd: 500, total_positions: 500, release_interval_hours: 0.5 },
];

// Auto-format release interval: minutes for sub-hour values (matches the
// admin Subpack Settings UI minutes-native paradigm), hours for >=1h.
// Both forms display whole numbers \u2014 spec ladder is integer-friendly
// in both ranges (32h, 16h, ..., 30min, 6min, 1min).
const formatInterval = (h) => {
  const n = Number(h);
  if (!Number.isFinite(n) || n <= 0) return '\u2014';
  if (n < 1) {
    const mins = Math.round(n * 60);
    return `${mins} min`;
  }
  // Whole hours when possible, otherwise 1 decimal.
  return Number.isInteger(n) ? `${n}h` : `${n.toFixed(1)}h`;
};

// Money on this page always renders with two decimals via fmtUsd
// ("$5.00", "$0.08"). A negative or NaN price renders "$0.00".
const formatMoneyShort = (v) => {
  const n = Number(v);
  return fmtUsd(Number.isFinite(n) && n > 0 ? n : 0);
};
// Speedy Pack gate — the API marks the Speedy Pack tiers
// (``requires_one_year_sub_pack``) and says whether this member holds
// the active 1-Year Sub Pack that unlocks them. The purchase route is
// what enforces it; here it only decides which tiers are greyed out.
// An older API without the field marks nothing, so nothing is locked.
const SPEEDY_PACK_LOCKED_CODE = 'SPEEDY_PACK_REQUIRES_ONE_YEAR_SUB_PACK';
const isSpeedyTier = (t) => t?.requires_one_year_sub_pack === true;
const packName = (speedy) => (speedy ? 'Speedy Pack' : 'Sub Pack');
// A 1-Year Sub Pack bought a moment ago can be missing from the next
// tier fetch (the panel reloads right after a buy, and the server's
// pack lookup may not have caught up yet). For one minute after such a
// buy the Speedy Pack tiers stay unlocked on this page regardless.
const ONE_YEAR_PACK_GRACE_MS = 60000;
let oneYearPackBoughtAtMs = 0;

const formatMoneyLong = (v) => {
  const n = Number(v);
  return fmtUsd(Number.isFinite(n) && n >= 0 ? n : 0);
};

/**
 * Existing crypto-deposit pending placeholder. Untouched copy — fires only
 * when LIVE_PAYMENTS_ENABLED is false on the backend.
 */
const DepositPendingPanel = () => (
  <section className="sc-glass-strong rounded-2xl p-7 md:p-10 relative overflow-hidden" data-testid="purchase-pending-panel">
    <div
      className="absolute -top-20 -right-20 w-80 h-80 rounded-full pointer-events-none"
      style={{
        background: 'radial-gradient(circle, rgba(255,182,18,0.18), transparent 60%)',
      }}
    />
    <div className="relative z-10 max-w-2xl">
      <div className="sc-pill">
        <span className="sc-dot" />
        Crypto Deposit
      </div>
      <h2 className="font-display font-bold text-2xl md:text-3xl mt-4">Deposits will be available shortly</h2>
      <p className="text-[15px] text-[var(--sc-text-dim)] mt-3 leading-relaxed">
        Your platform admin is finalizing the secure crypto payment provider connection. Once activated, you&apos;ll be able to deposit in{' '}
        <strong className="text-[#FFC20E]">LTC</strong>, <strong className="text-[#FFC20E]">TRX</strong>, and <strong className="text-[#FFC20E]">DOGE</strong>{' '}
        directly from this page.
      </p>
      <div className="mt-6 flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">
        <Clock className="w-3.5 h-3.5 text-[#FFC20E]" />
        Activation pending
      </div>
      <div
        className="mt-6 rounded-xl border border-[rgba(255,182,18,0.18)] bg-[rgba(17,17,17,0.45)] p-4 flex items-start gap-3"
        data-testid="purchase-pending-explainer"
      >
        <Wallet className="w-4 h-4 mt-0.5 text-[#FFC20E] shrink-0" />
        <div className="text-[13px] text-[var(--sc-text-dim)] leading-relaxed">
          In the meantime, you can use your existing Purchase Balance below to secure positions and Sub Packs.
        </div>
      </div>
      <div className="mt-3 rounded-xl border border-[rgba(255,182,18,0.18)] bg-[rgba(17,17,17,0.45)] p-4 flex items-start gap-3">
        <ShieldCheck className="w-4 h-4 mt-0.5 text-[#FFC20E] shrink-0" />
        <div className="text-[13px] text-[var(--sc-text-dim)] leading-relaxed">
          Funds are always held in your own wallet up to the moment you deposit. The platform never custodies more than a single payout batch at a time.
        </div>
      </div>
    </div>
  </section>
);

/**
 * Spend-from-purchase-balance action panel. Buy Position + Buy Sub Pack
 * actions, lifted verbatim from the old DualWalletCards Dashboard widget.
 * Polls /api/pool/status every 6s — but ONLY while this subpage is open,
 * thanks to React's useEffect cleanup. Dashboard view is unaffected.
 */
const BuyActionsPanel = ({ me, onRefresh }) => {
  const [buying, setBuying] = useState(false);
  const [subpackBusy, setSubpackBusy] = useState(0);
  const [poolEmpty, setPoolEmpty] = useState(false);
  // Live tier ladder fetched from backend. Falls back to the static
  // constant if the API call fails so the dropdown is never empty.
  const [tiers, setTiers] = useState(SUBPACK_TIERS_FALLBACK);
  // Patch v18.3 (bug-fix) — live single-position price. Rendered on the
  // "Buy Single Position" button, the explainer copy, and the confirm
  // modal. Populated from GET /api/me/subpacks/tiers on mount. Falls
  // back to "1.00" only if the API omits the field (older backend /
  // network hiccup) so the UI never renders "$NaN". The actual debit
  // amount is ALWAYS computed backend-side from runtime_config, so this
  // value is DISPLAY-ONLY \u2014 a stale/wrong value here can never
  // over- or under-charge a user.
  const [positionPriceUsd, setPositionPriceUsd] = useState('1.00');
  // Speedy Pack gate. Starts unlocked so nothing flashes locked before
  // the tier fetch answers; the purchase route refuses regardless.
  const [hasOneYearPack, setHasOneYearPack] = useState(true);
  // Confirmation modal state. ``pendingTier`` holds the tier object the
  // user just clicked from the dropdown; ``null`` means modal closed.
  // Purchase fires ONLY when the user clicks the AlertDialogAction button
  // inside the modal \u2014 not on the dropdown item click itself.
  const [pendingTier, setPendingTier] = useState(null);
  // Patch v17.6 cosmetic fix — controlled open state for the Buy Sub Pack
  // dropdown so we can programmatically close it the instant a tier is
  // selected (before the confirmation AlertDialog animates in). Prevents
  // the tier list from visually overlaying the modal on mobile / narrow
  // viewports where Radix's default portal stacking places the menu on
  // top of the dialog.
  const [menuOpen, setMenuOpen] = useState(false);

  // Single-position confirmation modal state. Same intercept pattern as
  // the Sub Pack flow: clicking Buy Single Position opens the modal,
  // and the actual buyPosition() call only fires from inside Confirm.
  const [singleBuyConfirmOpen, setSingleBuyConfirmOpen] = useState(false);
  // Live-ticking cooldown for the MANUAL single-position purchase rate
  // limit (1 buy per 5 min, enforced server-side, mirrored here for UX).
  // cooldownEndsAt is a Date ms epoch; 0 means no cooldown active.
  // Initially seeded from me.last_manual_position_buy_at so a fresh
  // page load picks up the timer mid-countdown.
  const cooldownSeconds = Number(me?.manual_position_buy_cooldown_seconds) || 300;
  const [cooldownEndsAt, setCooldownEndsAt] = useState(() => {
    const last = me?.last_manual_position_buy_at;
    if (!last) return 0;
    const t = Date.parse(last);
    if (Number.isNaN(t)) return 0;
    const end = t + cooldownSeconds * 1000;
    return end > Date.now() ? end : 0;
  });
  const [, forceTick] = useState(0);
  // ``nowMs`` is the rendering coroutine's view of current time. It only
  // updates inside the tick interval, so the render pass itself stays
  // pure (no Date.now() calls during render, which would trip the
  // ``react-hooks/purity`` rule). When the cooldown ends, the tick
  // interval clears ``cooldownEndsAt`` to 0, which keeps subsequent
  // renders idempotent.
  const [nowMs, setNowMs] = useState(() => Date.now());
  /* eslint-disable */
  useEffect(() => {
    // Tick every second while a cooldown is active so the displayed
    // M:SS pill stays current. Unmount-safe via the alive flag. The
    // ``set-state-in-effect`` lint rule (newer eslint-plugin-react-hooks)
    // mis-fires on interval-callback setState which is the canonical
    // React pattern for live timers \u2014 disabled at the block level so
    // both CRA's older eslint AND the newer linter pass.
    if (cooldownEndsAt === 0) return undefined;
    let alive = true;
    setNowMs(Date.now());
    const id = setInterval(() => {
      if (!alive) return;
      const t = Date.now();
      if (t >= cooldownEndsAt) {
        setCooldownEndsAt(0);
        setNowMs(t);
      } else {
        setNowMs(t);
        forceTick((n) => (n + 1) % 1000);
      }
    }, 1000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [cooldownEndsAt]);
  /* eslint-enable */
  const cooldownRemainingSec = Math.max(0, Math.ceil((cooldownEndsAt - nowMs) / 1000));
  const cooldownActive = cooldownRemainingSec > 0;
  const cooldownLabel = cooldownActive ? `${Math.floor(cooldownRemainingSec / 60)}:${String(cooldownRemainingSec % 60).padStart(2, '0')}` : '';

  useEffect(() => {
    let alive = true;
    const probe = async () => {
      try {
        const p = await getPoolStatus();
        if (alive) setPoolEmpty(!!p.is_empty);
      } catch {
        /* non-critical */
      }
    };
    probe();
    // Slow-poll (2025-06): pool-empty banner refresh once per minute.
    const t = setInterval(probe, 60000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  useEffect(() => {
    // One-shot live-tier fetch on mount. Failure leaves the static
    // SUBPACK_TIERS_FALLBACK in place \u2014 the dropdown still works,
    // it just shows the spec defaults instead of operator overrides.
    let alive = true;
    (async () => {
      try {
        const r = await fetchSubpackTiers();
        if (!alive) return;
        if (Array.isArray(r?.tiers) && r.tiers.length > 0) {
          setTiers(r.tiers);
        }
        if (typeof r?.has_active_one_year_sub_pack === 'boolean') {
          setHasOneYearPack(r.has_active_one_year_sub_pack || Date.now() - oneYearPackBoughtAtMs < ONE_YEAR_PACK_GRACE_MS);
        }
        // Patch v18.3 (bug-fix) — pull the live single-position price
        // alongside the tier list. Any parsing failure keeps the "1.00"
        // fallback so the button never renders a broken string.
        if (r?.position_price_usd != null) {
          const n = Number(r.position_price_usd);
          if (Number.isFinite(n) && n > 0) {
            setPositionPriceUsd(String(r.position_price_usd));
          }
        }
      } catch {
        /* keep fallback */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Pre-launch status — disables both buy buttons + shows a banner when ON.
  // Polls every 30 s so an admin toggle propagates without page reload.
  // Server-side enforcement is the source of truth; this gate is UX only.
  const [preLaunch, setPreLaunch] = useState({
    pre_launch_mode_enabled: false,
    launch_at_iso: null,
  });
  useEffect(() => {
    let alive = true;
    const probe = async () => {
      try {
        const s = await fetchPublicLaunchStatus();
        if (alive) {
          setPreLaunch({
            pre_launch_mode_enabled: !!s?.pre_launch_mode_enabled,
            launch_at_iso: s?.launch_at_iso || null,
          });
        }
      } catch {
        /* non-critical \u2014 default stays "not pre-launch" */
      }
    };
    probe();
    // Slow-poll (2025-06): pre-launch toggle propagation once per minute.
    const t = setInterval(probe, 60000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);
  const isPreLaunch = preLaunch.pre_launch_mode_enabled;

  const handleBuy = async () => {
    // Direct call path — only invoked from inside the confirmation modal's
    // Confirm action. The Buy Single Position BUTTON itself opens the
    // modal first via setSingleBuyConfirmOpen(true).
    setBuying(true);
    try {
      const r = await buyPosition('purchase_balance');
      if (r?.was_genesis_seed) {
        toast.success(`Genesis Seed Position created — ${r.status}`);
      } else {
        toast.success(`Position created (${r.status})`);
      }
      // Start the local cooldown timer immediately (server already
      // stamped the timestamp on its side). Uses the server-supplied
      // cooldown_seconds value so any future change is auto-respected.
      const cd = Number(r?.cooldown_seconds) || cooldownSeconds || 300;
      setCooldownEndsAt(Date.now() + cd * 1000);
      onRefresh?.();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // Server-side cooldown gate — re-sync our local timer from the
      // authoritative remaining_seconds field returned by the route.
      if (status === 429 && typeof detail === 'object' && detail?.remaining_seconds != null) {
        setCooldownEndsAt(Date.now() + Number(detail.remaining_seconds) * 1000);
        toast.error(detail.message || 'Manual buy cooldown active');
      } else {
        const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || err.message;
        toast.error(`Buy failed: ${msg}`);
      }
    } finally {
      setBuying(false);
    }
  };

  const handleBuySubpack = async (tierPrice, speedy = false) => {
    setSubpackBusy(tierPrice);
    try {
      const r = await buySubpack(tierPrice, 'purchase_balance');
      const positions = r?.total_positions ?? tierPrice;
      toast.success(`${fmtUsd(tierPrice)} ${packName(speedy)} activated — ${positions} positions queued`);
      // A 1-Year Sub Pack is what unlocks the Speedy Pack tiers: unlock
      // them now rather than waiting for the next page load.
      if (!speedy) {
        oneYearPackBoughtAtMs = Date.now();
        setHasOneYearPack(true);
      }
      onRefresh?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || err.message;
      if (typeof detail === 'object' && detail?.code === SPEEDY_PACK_LOCKED_CODE) {
        // The server's own sentence, as-is, and the tiers lock to match.
        oneYearPackBoughtAtMs = 0;
        setHasOneYearPack(false);
        toast.error(msg);
      } else {
        toast.error(`${packName(speedy)} buy failed: ${msg}`);
      }
    } finally {
      setSubpackBusy(0);
    }
  };

  const balance = Number(me?.purchase_balance || 0);

  // Speedy Pack gate — locked until the member holds an active 1-Year
  // Sub Pack. While locked, the 1-Year tiers lead the list (they are the
  // way in); once unlocked the withdrawal-eligible tiers lead, as before.
  const hasSpeedyTiers = tiers.some(isSpeedyTier);
  const speedyLocked = hasSpeedyTiers && !hasOneYearPack;
  const sortedTiers = [...tiers].sort((a, b) => {
    const ae = typeof a.withdrawal_eligible === 'boolean' ? a.withdrawal_eligible : true;
    const be = typeof b.withdrawal_eligible === 'boolean' ? b.withdrawal_eligible : true;
    if (ae !== be) return (ae ? -1 : 1) * (speedyLocked ? -1 : 1);
    return Number(a.tier_price_usd) - Number(b.tier_price_usd);
  });

  return (
    <section className="sc-glass-strong rounded-2xl p-5 md:p-7 relative overflow-hidden" data-testid="purchase-buy-actions">
      <div
        className="absolute -top-16 -right-16 w-56 h-56 pointer-events-none rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(255,182,18,0.14), transparent 65%)',
        }}
        aria-hidden="true"
      />

      <div className="relative z-10 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Spend From Purchase Balance</div>
          <div className="sc-tabular font-display font-bold text-3xl md:text-4xl mt-2 leading-none sc-gold-text" data-testid="purchase-spend-balance">
            {fmtUsd(balance)}
          </div>
          <p className="mt-2 text-[12px] text-[var(--sc-text-dim)] leading-snug max-w-md">
            Spend your existing Purchase Balance to secure a single position ({formatMoneyShort(positionPriceUsd)}) or to activate a Sub Pack for compounding
            bulk position releases.
          </p>
        </div>
        {poolEmpty && (
          <span
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] text-[#FFE08A] bg-[rgba(255,182,18,0.08)] border border-[rgba(255,182,18,0.32)] px-2.5 py-1 rounded-full shrink-0"
            data-testid="genesis-seed-pill"
          >
            <Sparkles className="w-3 h-3" />
            Genesis Seed Available
          </span>
        )}
      </div>

      {isPreLaunch ? (
        <div
          className="relative z-10 mt-5 rounded-xl px-4 py-3 flex items-start gap-3"
          style={{
            background: 'rgba(255, 99, 71, 0.08)',
            border: '1px solid rgba(255, 99, 71, 0.42)',
          }}
          data-testid="purchase-pre-launch-banner"
        >
          <Clock className="w-4 h-4 mt-0.5 shrink-0" style={{ color: '#FF6347' }} />
          <div className="text-[12.5px] leading-relaxed" style={{ color: 'var(--sc-text-dim)' }}>
            <span style={{ color: '#FF6347', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', fontSize: 11 }}>Pre-Launch Mode</span>
            <div className="mt-0.5">
              Sub Pack and Single Position purchases are temporarily paused. Deposits remain open so you can fund your wallet ahead of launch. We&apos;ll
              re-enable purchases as soon as the platform is live.
            </div>
          </div>
        </div>
      ) : null}

      <div className="relative z-10 mt-6 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setSingleBuyConfirmOpen(true)}
          disabled={buying || cooldownActive || isPreLaunch}
          className="sc-btn-white"
          style={{ padding: '12px 18px', fontSize: 12 }}
          data-testid="buy-position-btn"
          title={
            isPreLaunch
              ? 'Purchases are disabled \u2014 platform is in pre-launch mode'
              : cooldownActive
                ? `Cooldown active \u2014 next manual buy in ${cooldownLabel}`
                : undefined
          }
        >
          {buying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShoppingCart className="w-3.5 h-3.5" />}
          Buy Single Position ({formatMoneyShort(positionPriceUsd)})
        </button>

        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              disabled={subpackBusy > 0 || isPreLaunch}
              className="sc-btn-ghost"
              style={{ padding: '12px 18px', fontSize: 12 }}
              data-testid="buy-subpack-trigger"
              title={isPreLaunch ? 'Purchases are disabled \u2014 platform is in pre-launch mode' : undefined}
            >
              {subpackBusy > 0 ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Package className="w-3.5 h-3.5" />}
              Buy Sub Pack
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            sideOffset={6}
            className="sc-glass-strong w-[300px] p-2 border border-[rgba(255,182,18,0.18)] bg-[rgba(10,10,11,0.95)] text-[var(--sc-text)] z-[60]"
            data-testid="buy-subpack-menu"
          >
            <div className="px-2 py-1.5 text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Pick a Tier · From Purchase Balance</div>
            {speedyLocked && (
              <div
                className="mx-1 mb-2 rounded-lg px-3 py-2.5 flex items-start gap-2 text-[11px] leading-snug"
                style={{ background: 'rgba(255,182,18,0.08)', border: '1px solid rgba(255,182,18,0.42)', color: '#FFE08A' }}
                role="note"
                data-testid="buy-subpack-speedy-locked-notice"
              >
                <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#FFC20E]" />
                <span>
                  <strong>Speedy Packs are locked.</strong> You must have an active 1-Year Sub Pack before you can purchase a Speedy Pack. Pick a 1-Year Sub
                  Pack below to unlock them.
                </span>
              </div>
            )}
            <div className="rounded-lg border border-[rgba(255,182,18,0.10)] bg-[rgba(0,0,0,0.4)] overflow-hidden">
              {/* Patch v18.4 — withdrawal-eligible tiers lead the dropdown,
                  price-ascending within each group. Missing
                  withdrawal_eligible on a tier row is treated as TRUE
                  (matches backend legacy semantics). While the Speedy Packs
                  are locked the 1-Year tiers lead instead (sortedTiers). */}
              {sortedTiers.map((t, index) => {
                const tierPrice = Number(t.tier_price_usd);
                const positions = Number(t.total_positions);
                const intervalH = Number(t.release_interval_hours);
                const isEligible = typeof t.withdrawal_eligible === 'boolean' ? t.withdrawal_eligible : true;
                const speedy = isSpeedyTier(t);
                const locked = speedy && speedyLocked;
                const canAfford = balance >= tierPrice;
                const isBusyTier = subpackBusy === tierPrice;
                const isAnyBusy = subpackBusy > 0;
                const disabled = locked || !canAfford || isAnyBusy;
                const startsGroup = hasSpeedyTiers && (index === 0 || isSpeedyTier(sortedTiers[index - 1]) !== speedy);
                return (
                  <Fragment key={tierPrice}>
                    {startsGroup && (
                      <div
                        className="px-3 py-1.5 text-[9.5px] uppercase tracking-[0.2em] text-[var(--sc-text-mute)] bg-[rgba(255,182,18,0.05)] border-b border-[rgba(255,182,18,0.08)] flex items-center gap-1.5"
                        data-testid={speedy ? 'buy-subpack-group-speedy' : 'buy-subpack-group-one-year'}
                      >
                        {speedy ? (
                          <>
                            {locked && <Lock className="w-2.5 h-2.5 text-[#FFC20E]" />}
                            Speedy Packs{locked ? ' · Locked' : ''}
                          </>
                        ) : (
                          '1-Year Sub Packs'
                        )}
                      </div>
                    )}
                    <DropdownMenuItem
                      disabled={disabled}
                      onSelect={(e) => {
                        e.preventDefault();
                        setMenuOpen(false);
                        setPendingTier({
                          tier_price_usd: tierPrice,
                          total_positions: positions,
                          release_interval_hours: intervalH,
                          withdrawal_eligible: isEligible,
                          speedy,
                        });
                      }}
                      className={`w-full px-3 py-2.5 text-[12px] flex items-center justify-between gap-3 border-b border-[rgba(255,182,18,0.05)] last:border-b-0 rounded-none cursor-pointer ${
                        disabled
                          ? isEligible
                            ? 'text-[#FFE08A] opacity-70 data-[disabled]:!opacity-70'
                            : 'text-[var(--sc-text-mute)] data-[disabled]:!opacity-100'
                          : isEligible
                            ? 'text-[#FFE08A] focus:bg-[rgba(255,224,138,0.14)] focus:text-[#FFF3B0] data-[highlighted]:bg-[rgba(255,224,138,0.14)] data-[highlighted]:text-[#FFF3B0]'
                            : 'text-[var(--sc-text)] focus:bg-[rgba(255,182,18,0.08)] focus:text-[#FFC20E] data-[highlighted]:bg-[rgba(255,182,18,0.08)] data-[highlighted]:text-[#FFC20E]'
                      }`}
                      data-testid={`buy-subpack-${tierPrice}`}
                    >
                      <span className="flex flex-col min-w-0">
                        <span className={`sc-tabular ${isEligible ? 'font-bold' : 'font-semibold'}`}>
                          {fmtUsd(tierPrice)} {packName(speedy)}
                          {isEligible && (
                            <span
                              className="ml-2 inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[8.5px] uppercase tracking-[0.14em] font-medium align-middle"
                              style={{
                                backgroundColor: 'rgba(94,230,161,0.14)',
                                color: '#5EE6A1',
                                border: '1px solid rgba(94,230,161,0.42)',
                              }}
                              data-testid={`buy-subpack-eligible-badge-${tierPrice}`}
                            >
                              <Unlock className="w-2.5 h-2.5" />
                              Withdrawal
                            </span>
                          )}
                        </span>
                        <span className="text-[10px] text-[var(--sc-text-mute)] mt-0.5">
                          {positions} positions · 1 every {formatInterval(intervalH)}
                        </span>
                        {locked && (
                          <span className="text-[10px] text-[#FFC20E] mt-0.5" data-testid={`buy-subpack-locked-reason-${tierPrice}`}>
                            Requires an active 1-Year Sub Pack
                          </span>
                        )}
                      </span>
                      <span className="text-[11px] flex items-center shrink-0">
                        {isBusyTier ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin text-[#FFC20E]" />
                        ) : locked ? (
                          <span
                            className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.18em]"
                            data-testid={`buy-subpack-locked-${tierPrice}`}
                          >
                            <Lock className="w-3 h-3" />
                            Locked
                          </span>
                        ) : !canAfford ? (
                          <span className="text-[9px] uppercase tracking-[0.18em]">Low Bal</span>
                        ) : (
                          <span className="text-[#FFC20E]">→</span>
                        )}
                      </span>
                    </DropdownMenuItem>
                  </Fragment>
                );
              })}
            </div>
            <div className="px-2 py-2 text-[10px] text-[var(--sc-text-mute)] leading-snug">
              Positions drip on the tier&apos;s schedule. PIF a Sub Pack via the Global PIF Pool page.
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Manual single-position cooldown pill. Sits directly under the
          Buy Single Position button. Hidden when no cooldown active so
          the section stays compact in normal use. Auto-repurchase is
          completely unaffected by this UI element (or the server gate
          backing it) — that path runs through positions_svc.system_auto_repurchase
          and bypasses the rate-limited route. */}
      {cooldownActive && (
        <div
          className="relative z-10 mt-3 inline-flex items-center gap-2 text-[11px] rounded-full px-3 py-1.5 border border-[rgba(255,182,18,0.32)] bg-[rgba(255,182,18,0.06)] text-[var(--sc-text-dim)]"
          data-testid="buy-position-cooldown"
        >
          <Clock className="w-3 h-3 text-[#FFC20E]" />
          <span>
            Next manual buy in{' '}
            <span className="font-mono font-semibold text-[#FFE08A] sc-tabular" data-testid="buy-position-cooldown-timer">
              {cooldownLabel}
            </span>
          </span>
        </div>
      )}

      {/* Single-position purchase confirmation modal. Same intercept
          pattern as the Sub Pack modal: clicking Buy Single Position
          opens this modal; the actual buyPosition() API call only fires
          when the user clicks the Confirm action inside. */}
      <AlertDialog
        open={singleBuyConfirmOpen}
        onOpenChange={(open) => {
          // Block dismiss while a purchase is in flight to avoid an
          // orphaned mid-call state.
          if (!open && !buying) setSingleBuyConfirmOpen(false);
        }}
      >
        <AlertDialogContent
          className="sc-glass-strong border border-[rgba(255,182,18,0.18)] bg-[rgba(10,10,11,0.96)] text-[var(--sc-text)] max-w-md"
          data-testid="buy-position-confirm-modal"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-[#FFE08A] text-lg flex items-center gap-2">
              <ShoppingCart className="w-4 h-4 text-[#FFC20E]" />
              Confirm Single Position Purchase
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--sc-text-dim)] text-[13px] leading-relaxed">
              You are about to buy <strong className="text-[#FFE08A]">1 single position</strong> for{' '}
              <strong className="text-[#FFE08A]">{formatMoneyLong(positionPriceUsd)}</strong> from your Purchase Balance.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="rounded-xl border border-[rgba(255,182,18,0.18)] bg-[rgba(0,0,0,0.42)] p-4 space-y-2 my-2" data-testid="buy-position-confirm-summary">
            <div className="flex items-center justify-between text-[12px]">
              <span className="text-[var(--sc-text-mute)] uppercase tracking-[0.18em] text-[10px]">Cost</span>
              <span className="sc-tabular font-semibold text-[#FFE08A]">{formatMoneyLong(positionPriceUsd)}</span>
            </div>
            <div className="flex items-center justify-between text-[12px] pt-2 mt-2 border-t border-[rgba(255,182,18,0.12)]">
              <span className="text-[var(--sc-text-mute)] uppercase tracking-[0.18em] text-[10px]">Balance After</span>
              <span className="sc-tabular font-semibold text-[#5EE6A1]">{fmtUsd(Math.max(0, balance - Number(positionPriceUsd || 0)))}</span>
            </div>
          </div>
          <p className="text-[11px] text-[var(--sc-text-mute)] leading-snug">
            Manual buys are rate-limited to 1 every {Math.round(cooldownSeconds / 60)} minutes.
          </p>
          <AlertDialogFooter className="mt-3">
            <AlertDialogCancel
              disabled={buying}
              className="bg-transparent border border-[rgba(255,182,18,0.32)] text-[var(--sc-text-dim)] hover:border-[#FFC20E] hover:text-[#FFE08A] hover:bg-[rgba(255,182,18,0.04)] disabled:opacity-40"
              data-testid="buy-position-confirm-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={buying}
              onClick={async (e) => {
                e.preventDefault();
                await handleBuy();
                setSingleBuyConfirmOpen(false);
              }}
              className="bg-[#FFC20E] text-black hover:bg-[#FFE08A] font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="buy-position-confirm-action"
            >
              {buying ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Buying...
                </span>
              ) : (
                <>Confirm &amp; Buy Position</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Sub Pack purchase confirmation modal. Opens when the user clicks
          a tier in the dropdown; the actual buySubpack API call only fires
          when the user explicitly clicks "Confirm & Activate" inside this
          modal. Cancel / outside-click / Esc all dismiss without buying. */}
      <AlertDialog
        open={pendingTier !== null}
        onOpenChange={(open) => {
          // Block dismiss while a purchase is in flight to avoid orphaned
          // mid-call state. Once the request completes (success or fail),
          // ``setSubpackBusy(0)`` runs in the finally block and the modal
          // can close normally.
          if (!open && subpackBusy === 0) setPendingTier(null);
        }}
      >
        <AlertDialogContent
          className="sc-glass-strong border border-[rgba(255,182,18,0.18)] bg-[rgba(10,10,11,0.96)] text-[var(--sc-text)] max-w-md"
          data-testid="buy-subpack-confirm-modal"
        >
          {pendingTier && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle className="font-display text-[#FFE08A] text-lg flex items-center gap-2">
                  <Package className="w-4 h-4 text-[#FFC20E]" />
                  Confirm {packName(pendingTier.speedy)} Purchase
                </AlertDialogTitle>
                <AlertDialogDescription className="text-[var(--sc-text-dim)] text-[13px] leading-relaxed">
                  You are about to purchase a{' '}
                  <strong className="text-[#FFE08A]">
                    {fmtUsd(pendingTier.tier_price_usd)} {packName(pendingTier.speedy)}
                  </strong>{' '}
                  from your Purchase Balance.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div
                className="rounded-xl border border-[rgba(255,182,18,0.18)] bg-[rgba(0,0,0,0.42)] p-4 space-y-2 my-2"
                data-testid="buy-subpack-confirm-summary"
              >
                <div className="flex items-center justify-between text-[12px]">
                  <span className="text-[var(--sc-text-mute)] uppercase tracking-[0.18em] text-[10px]">Cost</span>
                  <span className="sc-tabular font-semibold text-[#FFE08A]">{fmtUsd(pendingTier.tier_price_usd)}</span>
                </div>
                <div className="flex items-center justify-between text-[12px]">
                  <span className="text-[var(--sc-text-mute)] uppercase tracking-[0.18em] text-[10px]">Positions</span>
                  <span className="sc-tabular font-medium">{pendingTier.total_positions}</span>
                </div>
                <div className="flex items-center justify-between text-[12px]">
                  <span className="text-[var(--sc-text-mute)] uppercase tracking-[0.18em] text-[10px]">Drip Cadence</span>
                  <span className="sc-tabular font-medium">1 every {formatInterval(pendingTier.release_interval_hours)}</span>
                </div>
                <div className="flex items-center justify-between text-[12px] pt-2 mt-2 border-t border-[rgba(255,182,18,0.12)]">
                  <span className="text-[var(--sc-text-mute)] uppercase tracking-[0.18em] text-[10px]">Balance After</span>
                  <span className="sc-tabular font-semibold text-[#5EE6A1]">{fmtUsd(balance - pendingTier.tier_price_usd)}</span>
                </div>
              </div>
              <p className="text-[11px] text-[var(--sc-text-mute)] leading-snug">
                The first position drops <strong>immediately</strong> on activation. Subsequent positions release on the cadence shown above.
              </p>
              <AlertDialogFooter className="mt-3">
                <AlertDialogCancel
                  disabled={subpackBusy !== 0}
                  className="bg-transparent border border-[rgba(255,182,18,0.32)] text-[var(--sc-text-dim)] hover:border-[#FFC20E] hover:text-[#FFE08A] hover:bg-[rgba(255,182,18,0.04)] disabled:opacity-40"
                  data-testid="buy-subpack-confirm-cancel"
                >
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  disabled={subpackBusy !== 0}
                  onClick={async (e) => {
                    e.preventDefault();
                    const tp = pendingTier.tier_price_usd;
                    await handleBuySubpack(tp, pendingTier.speedy);
                    setPendingTier(null);
                  }}
                  className="bg-[#FFC20E] text-black hover:bg-[#FFE08A] font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="buy-subpack-confirm-action"
                >
                  {subpackBusy === pendingTier.tier_price_usd ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Activating...
                    </span>
                  ) : (
                    <>
                      Confirm &amp; Activate {fmtUsd(pendingTier.tier_price_usd)} {packName(pendingTier.speedy)}
                    </>
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};

/**
 * Purchase / Deposit subpage — combines two distinct concerns:
 *   1. Crypto-deposit pending placeholder (waiting on NOWPayments).
 *   2. Buy Position + Buy Sub Pack actions using existing Purchase Balance
 *      (always functional, this is where these utilities now live after
 *      the Dashboard slim-down refactor).
 */
const PurchaseSubpage = ({ me, onRefresh }) => {
  const [enabled, setEnabled] = useState(null);

  useEffect(() => {
    const backendUrl = (typeof process !== 'undefined' && process.env.REACT_APP_BACKEND_URL) || '';
    let alive = true;
    fetch(`${backendUrl}/api/public/config`, { credentials: 'omit' })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        if (alive) setEnabled(Boolean(d?.live_payments_enabled));
      })
      .catch(() => alive && setEnabled(false));
    return () => {
      alive = false;
    };
  }, []);

  if (enabled === null) {
    return (
      <div className="sc-glass rounded-2xl p-10 flex items-center justify-center gap-2 text-[var(--sc-text-dim)]" data-testid="subpage-purchase-loading">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="subpage-purchase">
      {/* Patch v18.0 — banned-account banner (renders null when not flagged). */}
      <BannedBanner me={me} />
      {enabled ? <CryptoDepositPanel /> : <DepositPendingPanel />}
      {/* Patch v13 — LiveDepositPanel (legacy sub-partner address flow) hidden in favor
          of CryptoDepositPanel. Commented (not deleted) for instant rollback if needed. */}
      {/* {enabled ? <LiveDepositPanel /> : <DepositPendingPanel />} */}
      {me && <BuyActionsPanel me={me} onRefresh={onRefresh} />}
    </div>
  );
};

export default PurchaseSubpage;
